import { Transformation } from 'app/model/dataflow/transformation';
import { AdapterContext, Dataset, LinkedService, Metamodel, IDataFlowDSLModelAdapter, ModelConstants } from 'shell-models-lib';
import { LinkedServiceUtils } from 'app/model/linkedservice/linked-service.utils';
import { DataFlowExpressionUtils } from 'app/model/adapters/dataflow/expression.util';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { ContainerType } from 'app/model/adapters/dataflow/IDataflow';
import { stringIsNullOrEmpty, string2UnicodeSingle } from 'app/shared/utils/string.util';
import { ArrayDataFlowType } from 'worker/app-workers/shared/models/dataflow-datatype';
import { getAttributeValueFromDataset } from 'app/model/dataset/dataset.utils';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { FileBasedSettings, FILEMODE } from 'app/model/dataflow/dataset-settings/file-based-settings';
import { DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';

export class DataFlowAdapterUtils {
    public static readonly REST_CALL_SPLITTER = /,(?=\s*(?:(?:\(.*\))|(?:'.*')|(?:\w+))\s*->)(?![^\(]*\))/g;
    public static readonly MAP_ARRAY_SPLITTER = /,(?=\s*(?:(?:\(.*\))|(?:'.*'))\s*->)(?![^\(]*\))/g;
    public static readonly MAP_KEY_VALUE_SPLITTER = /->(?![^\(]*\))/g;

    public static getUnknownProps(transformation: Transformation, transformationData: string): string {
        const unknownProps: string[] = [];
        const existingProps = transformationData.split(',\n\t');
        if (transformation.unknownProps.length) {
            const normalizedProps = existingProps.filter((prop) => !!prop).map((prop) => DataFlowAdapterUtils._getNormalizedPropName(prop));
            transformation.unknownProps.forEach((unknownProp) => {
                if (!normalizedProps.includes(DataFlowAdapterUtils._getNormalizedPropName(unknownProp))
                    && !unknownProps.includes(unknownProp)) {
                    unknownProps.push(unknownProp);
                }
            });
        }
        return unknownProps.join(',\n\t');
    }

    public static hasExpression(container: DataFlowExpression): boolean {
        return !stringIsNullOrEmpty(container.expression);
    }

    /**
     * When generating dataflow script, if the source or sink has container, a prop of the container type needs to be
     * added.
     *
     * @param linkedService
     * @param container
     * @param defaultContainerType
     */
    public static getContainerProp(
        linkedService: LinkedService,
        container: DataFlowExpression,
        defaultContainerType: ContainerType = ContainerType.CONTAINER
    ): string {
        let containerType = defaultContainerType;
        if (LinkedServiceUtils.isFileSystemTypeInBackend(linkedService)) {
            containerType = ContainerType.FILE_SYSTEM;
        } else if (LinkedServiceUtils.isBucketNameTypeInBackend(linkedService)) {
            containerType = ContainerType.BUCKET_NAME;
        }

        return `${containerType}: ${DataFlowExpressionUtils.wrapExpressionConfigValue(container)}`;
    }

    public static getPreSqlProps(setting: { isPreSqlsListOfExpression: boolean, preSqls: DataFlowExpression[] }): string[] {
        const props: string[] = [];
        switch (setting.isPreSqlsListOfExpression) {
            case false:
                if (setting.preSqls && setting.preSqls.length && setting.preSqls[0].expression) {
                    props.push(`preSQLs: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.preSqls[0], ArrayDataFlowType.TYPE)}`);
                }
                break;
            default:
                const preSQLScripts = (setting.preSqls || []).filter((script) => !!script.expression)
                    .map((script) => DataFlowExpressionUtils.escapeAndWrapConfigValue(script)).join(',');
                if (!!preSQLScripts) {
                    props.push(`preSQLs:[${preSQLScripts}]`);
                }
        }

        return props;
    }

    public static getPostSqlProps(setting: { isPostSqlsListOfExpression: boolean, postSqls: DataFlowExpression[] }): string[] {
        const props: string[] = [];
        switch (setting.isPostSqlsListOfExpression) {
            case false:
                if (setting.postSqls && setting.postSqls.length && setting.postSqls[0].expression) {
                    props.push(`postSQLs: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.postSqls[0], ArrayDataFlowType.TYPE)}`);
                }
                break;
            default:
                const postSQLScripts = (setting.postSqls || []).filter((script) => !!script.expression)
                    .map((script) => DataFlowExpressionUtils.escapeAndWrapConfigValue(script)).join(',');
                if (!!postSQLScripts) {
                    props.push(`postSQLs:[${postSQLScripts}]`);
                }
        }
        return props;
    }

    public static isStagingSchemaPropertyValidForSnowflake(linkedServiceType: string, stagingSchemaName: string): boolean {
        return ((linkedServiceType === adfModelTypeMap.snowflake.linkedServiceType || linkedServiceType === adfModelTypeMap.snowflakeV2.linkedServiceType) && !!stagingSchemaName);
    }

    /**
     * Gets the format adapter for a dataset type.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static getFormatAdapterForDataset(context: AdapterContext, dataset: Dataset): IDataFlowDSLModelAdapter<any> | undefined {
        const datasetTypeName = Metamodel.modelOf(dataset).name;

        // Special case: ParquetSettings has no adapter registered, use ParquetInlineSettings instead
        if (datasetTypeName == ModelConstants.parquetDataset) {
            return context.dataflowDSLAdapterContext.adapterFor(Metamodel.modelByName(adfModelTypeMap.parquetInline.dataflowSettings));
        }
        if (datasetTypeName === ModelConstants.lakehouseTableDataset) {
            return context.dataflowDSLAdapterContext.adapterFor(
                Metamodel.modelByName(adfModelTypeMap.delta.dataflowSettings)
            );
        }
        for (const entry of Object.values(adfModelTypeMap)) {
            const typedEntry = entry as { datasetType?: string; dataflowSettings?: string };
            if (typedEntry.datasetType === datasetTypeName && typedEntry.dataflowSettings) {
                try {
                    const model = Metamodel.modelByName(typedEntry.dataflowSettings);
                    if (model) {
                        const adapter = context.dataflowDSLAdapterContext.adapterFor(model);
                        if (!adapter) {
                            console.warn(`[DataFlowAdapterUtils] No adapter found for dataset type '${datasetTypeName}' with settings '${typedEntry.dataflowSettings}'`);
                        }
                        return adapter;
                    }
                } catch (error) {
                    // Model not registered, log and skip this entry
                    console.error(`[DataFlowAdapterUtils] No adapter registered for dataset type '${datasetTypeName}' with settings '${typedEntry.dataflowSettings}':`, error);
                }
            }
        }
        return undefined;
    }

    /**
     * Formats a delimiter value for DSL (handles unicode escaping).
     */
    public static formatDelimiterValue(value: string): string {
        const expr = new DataFlowExpression();
        expr.expression = JSON.stringify(string2UnicodeSingle(value)).slice(1, -1);
        return DataFlowExpressionUtils.wrapExpressionConfigValue(expr);
    }

    /**
     * Formats an expression value for DSL.
     */
    public static formatExpressionValue(value: string): string {
        const expr = new DataFlowExpression();
        expr.expression = value;
        return DataFlowExpressionUtils.wrapExpressionConfigValue(expr);
    }

    /**
     * Adds common ADLS format properties (container/fileSystem/bucketName, folderPath, fileName) from dataset for Fabric migration.
     */
    public static addCommonPropsForFileBasedFormats(
        dataset: Dataset, linkedService: LinkedService | undefined, props: string[], isSource: boolean,
        isInline: boolean, datasetSettings: DataFlowDatasetSettings | undefined, wildcardPaths?: DataFlowExpression[]
    ): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const location = (dataset as any)?.location?.getParameterizedLocation?.();
        if (location) {
            if (location.container) {
                const containerKey = LinkedServiceUtils.isBucketNameTypeInBackend(linkedService) ? 'bucketName'
                    : LinkedServiceUtils.isFileSystemTypeInBackend(linkedService) ? 'fileSystem' : 'container';
                props.push(`${containerKey}: ${DataFlowAdapterUtils.formatExpressionValue(location.container)}`);
            }

            if (isSource) {
                // Use the same check for wildcard detection as source.adapter.ts
                const couldUseWildcardPaths = !isInline || !datasetSettings ||
                    (datasetSettings instanceof FileBasedSettings && datasetSettings.fileMode === FILEMODE.WILDCARD);
                const hasWildcardPaths = couldUseWildcardPaths && !!wildcardPaths?.filter((path) => path.expression).length;

                if (!hasWildcardPaths) {
                    if (location.folderPath) {
                        props.push(`folderPath: ${DataFlowAdapterUtils.formatExpressionValue(location.folderPath)}`);
                    }
                    if (location.fileName) {
                        props.push(`fileName: ${DataFlowAdapterUtils.formatExpressionValue(location.fileName)}`);
                    }
                }
            } else {
                // Sinks: always add folderPath, never add fileName
                if (location.folderPath) {
                    props.push(`folderPath: ${DataFlowAdapterUtils.formatExpressionValue(location.folderPath)}`);
                }
            }
        }
    }

    /**
     * Supplements schemaName and tableName from a SQL-based dataset.
     */
    public static supplementSqlSchemaAndTableFromDataset(dataset: Dataset, props: string[], isSource: boolean, format?: string): void {
        if (isSource && format !== 'table') {
            return;
        }

        const schema = getAttributeValueFromDataset(dataset, 'schema');
        if (schema) {
            props.push(`schemaName: ${DataFlowAdapterUtils.formatExpressionValue(schema)}`);
        }

        const table = getAttributeValueFromDataset(dataset, 'table');
        if (table) {
            props.push(`tableName: ${DataFlowAdapterUtils.formatExpressionValue(table)}`);
        }
    }

    private static _getNormalizedPropName(prop: string): string {
        return prop.toLowerCase().substring(0, prop.indexOf(':'));
    }
}
